import { useState } from 'react';
import { colour, space, text } from '../../design/tokens.ts';
import { Button, Card, Field, Select, Status, Tag } from '../../components/basics.tsx';
import { CouldNotLoad, Loading } from '../../components/states.tsx';
import { api } from '../../api/client.ts';
import { useLoad } from '../../app/useLoad.ts';

/**
 * Admin → Unreal: what the Unreal plugin has reported, and what it means.
 *
 * Maestro never guesses either of these, so a person says each one once:
 *
 *   · which Maestro splat a splat source in Unreal is — until somebody says,
 *     a scene with that splat changes nothing, because placing cameras
 *     against the wrong splat would make every judgement about them wrong;
 *   · what an actor class is, beyond the splat players the plugin finds
 *     through a sequence's splat track by itself.
 *
 * Linking a source adds its Unreal path to the splat's current version. It
 * is not a new version, so nothing placed in the splat goes stale, and a
 * linked source is never moved to a different splat from here.
 */

interface SourceRow {
  asset_path: string;
  project_id: string;
  editorial?: { clip_id: number; solves?: { solve_id: number; type?: string }[] };
  last_seen_at: string;
  last_seen: { level: string; sequence?: string; machine: string };
  splat: { splat_id: string; version: number; variant?: string; take?: string } | null;
}

interface SplatChoice {
  splat_id: string;
  version: number;
  says: string;
}

interface UnrealProjectRow {
  project_id: string;
  name: string;
  path?: string;
  last_seen_at: string;
  last_seen: { machine: string; by?: string; sequence?: string };
}

interface ClassRow {
  class_name: string;
  means: 'splat' | 'camera' | 'ignore';
  confirmed_at?: string;
}

/** The asset name a person would recognise, not the whole path. */
const nameOf = (path: string): string => path.split('/').pop()?.split('.')[0] ?? path;

export function UnrealSection({ onSaid }: { onSaid: (message: string) => void }) {
  const sources = useLoad<{ sources: SourceRow[]; about: string }>(
    () => api.get('/api/admin/ue/splat-sources'),
    []
  );
  const unrealProjects = useLoad<{ unreal_projects: UnrealProjectRow[]; about: string }>(
    () => api.get('/api/admin/ue/unreal-projects'),
    []
  );
  const classes = useLoad<{ classes: ClassRow[]; about: string; means: { key: string; says: string }[] }>(
    () => api.get('/api/admin/ue/classes'),
    []
  );

  async function setMeans(name: string, means: string) {
    try {
      await api.put(`/api/admin/ue/classes/${encodeURIComponent(name)}`, { means });
      classes.reload();
      onSaid(`${name} is now known as ${means === 'ignore' ? 'nothing Maestro needs' : `a ${means}`}.`);
    } catch (err) {
      onSaid(err instanceof Error ? err.message : 'That change was refused.');
    }
  }

  return (
    <>
      <Card title="Unreal projects" aside={unrealProjects.state.data?.about}>
        {unrealProjects.state.status === 'failed' && (
          <CouldNotLoad what="the Unreal projects" error={unrealProjects.state.error} onRetry={unrealProjects.reload} />
        )}
        {unrealProjects.state.data?.unreal_projects.length === 0 && (
          <span style={{ ...text.small, color: colour.inkSoft }}>
            None yet. An Unreal project is added here by its first report, under the Maestro project chosen in the render dialog.
          </span>
        )}
        {(unrealProjects.state.data?.unreal_projects ?? []).map((row) => (
          <div key={`${row.project_id}/${row.name}`} style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
            <span style={text.bodyStrong}>{row.name}</span>
            <Tag>in {row.project_id}</Tag>
            {row.path && <Tag tone="mono">{row.path}</Tag>}
            <span style={{ ...text.note, color: colour.inkSoft }}>
              last reported {new Date(row.last_seen_at).toLocaleString()} from {row.last_seen.machine}
              {row.last_seen.sequence ? `, in ${row.last_seen.sequence}` : ''}
            </span>
          </div>
        ))}
      </Card>

      <Card title="Splats Unreal has reported" aside={sources.state.data?.about}>
        {sources.state.status === 'loading' && !sources.state.data && <Loading says="Loading splat sources" rows={2} />}
        {sources.state.status === 'failed' && (
          <CouldNotLoad what="the splat sources" error={sources.state.error} onRetry={sources.reload} />
        )}
        {sources.state.data?.sources.length === 0 && (
          <span style={{ ...text.small, color: colour.inkSoft }}>
            Nothing yet. They appear here the first time the plugin reports a scene with one in it.
          </span>
        )}
      </Card>
      {(sources.state.data?.sources ?? []).map((source) => (
        <SourceCard
          key={source.asset_path}
          source={source}
          onLinked={(row) => {
            sources.reload();
            onSaid(
              `${nameOf(row.asset_path)} is ${row.splat?.take ?? 'that splat'}. Report the scene again to place its cameras.`
            );
          }}
          onRefused={onSaid}
        />
      ))}

      <Card title="Actor classes" aside={classes.state.data?.about}>
        {classes.state.status === 'failed' && (
          <CouldNotLoad what="the actor classes" error={classes.state.error} onRetry={classes.reload} />
        )}
        <span style={{ ...text.small, color: colour.inkSoft }}>
          A splat player in a sequence's splat track is found without being listed here. These are the
          other classes the plugin has seen.
        </span>
        {(classes.state.data?.classes ?? []).map((row) => (
          <div key={row.class_name} style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
            <Tag tone="mono">{row.class_name}</Tag>
            <Select
              value={row.means}
              onChange={(means) => void setMeans(row.class_name, means)}
              options={(classes.state.data?.means ?? []).map((m) => ({ value: m.key as ClassRow['means'], label: m.key }))}
            />
            {!row.confirmed_at && <Tag>nobody has said yet</Tag>}
          </div>
        ))}
      </Card>
    </>
  );
}

function SourceCard({
  source,
  onLinked,
  onRefused
}: {
  source: SourceRow;
  onLinked: (row: SourceRow) => void;
  onRefused: (message: string) => void;
}) {
  const [choosing, setChoosing] = useState(false);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const splats = useLoad<{ splats: SplatChoice[] }>(
    () =>
      choosing
        ? api.get(`/api/splats?project_id=${encodeURIComponent(source.project_id)}`)
        : Promise.resolve({ splats: [] }),
    [choosing, source.project_id]
  );

  async function link() {
    if (!chosen) return;
    setBusy(true);
    try {
      const answer = await api.post<{ source: SourceRow }>('/api/admin/ue/splat-sources/link', {
        asset_path: source.asset_path,
        splat_id: chosen
      });
      setChoosing(false);
      onLinked(answer.source);
    } catch (err) {
      onRefused(err instanceof Error ? err.message : 'That link was refused.');
    } finally {
      setBusy(false);
    }
  }

  const seen = source.last_seen;
  return (
    <Card title={nameOf(source.asset_path)} aside={source.project_id}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
        {source.splat ? (
          <Tag>
            is {source.splat.take ?? 'a splat'}
            {source.splat.variant ? ` · ${source.splat.variant}` : ''} v{source.splat.version}
          </Tag>
        ) : (
          <Status value="blocked" detail="Maestro does not know which splat this is, so scenes with it change nothing" />
        )}
        {source.editorial && <Tag>Editorial clip {source.editorial.clip_id}</Tag>}
        {source.editorial?.solves?.map((s) => (
          <Tag key={s.solve_id}>
            solve {s.solve_id}
            {s.type ? ` · ${s.type}` : ''}
          </Tag>
        ))}
      </div>
      <Tag tone="mono">{source.asset_path}</Tag>
      <span style={{ ...text.note, color: colour.inkSoft }}>
        last reported {new Date(source.last_seen_at).toLocaleString()} from {seen.machine}
        {seen.sequence ? `, in ${seen.sequence}` : ''}
      </span>

      {!source.splat && !choosing && (
        <Button tone="primary" onClick={() => setChoosing(true)}>
          Say which splat this is
        </Button>
      )}
      {!source.splat && choosing && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'flex-end' }}>
          <Field label={`A splat in ${source.project_id}`}>
            <Select
              value={chosen}
              onChange={setChosen}
              options={[
                { value: '', label: splats.state.status === 'loading' ? 'Loading…' : 'Choose a splat' },
                ...(splats.state.data?.splats ?? []).map((s) => ({ value: s.splat_id, label: `${s.says} · v${s.version}` }))
              ]}
            />
          </Field>
          <Button
            tone="primary"
            disabled={!chosen || busy}
            disabledReason={busy ? 'Linking…' : 'Choose the splat first.'}
            onClick={() => void link()}
          >
            Link
          </Button>
          <Button onClick={() => setChoosing(false)}>Cancel</Button>
        </div>
      )}
    </Card>
  );
}
